/**
 * Currency utility functions
 */

// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  'EUR': '€',
  'USD': '$',
  'GBP': '£',
  'RSD': 'RSD',
  'HRK': 'kn',
  'HUF': 'Ft',
  'CZK': 'Kč',
  'RON': 'lei'
};

export const SUPPORTED_CURRENCIES = [
  { code: 'EUR', label: 'EUR (€)' },
  { code: 'USD', label: 'USD ($)' },
  { code: 'GBP', label: 'GBP (£)' },
  { code: 'RSD', label: 'RSD' },
  { code: 'HRK', label: 'HRK (kn)' },
  { code: 'HUF', label: 'HUF (Ft)' },
  { code: 'CZK', label: 'CZK (Kč)' },
  { code: 'RON', label: 'RON (lei)' },
];

export const LANGUAGE_DEFAULT_CURRENCY = {
  en: 'EUR',
  sr: 'RSD',
  hr: 'EUR',
  ro: 'RON',
  hu: 'HUF',
  cs: 'CZK',
  sk: 'EUR',
  uk: 'EUR',
};

export function getDefaultCurrencyForLanguage(languageCode) {
  return LANGUAGE_DEFAULT_CURRENCY[languageCode] || 'EUR';
}

// Rough rates: units of each currency per 1 EUR (for pre-filling conversion; user can edit)
const UNITS_PER_EUR = {
  EUR: 1,
  USD: 1.08,
  GBP: 0.86,
  RSD: 117,
  HRK: 7.53,
  HUF: 395,
  CZK: 25.3,
  RON: 4.97,
};

function formatDefaultRate(rate) {
  if (rate >= 100) return String(Math.round(rate));
  if (rate >= 10) return String(Math.round(rate * 10) / 10);
  if (rate >= 1) return String(Math.round(rate * 100) / 100);
  if (rate >= 0.01) return String(Math.round(rate * 10000) / 10000);
  return String(Math.round(rate * 1000000) / 1000000);
}

export function getDefaultConversionRate(fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return '1';

  const fromUnits = UNITS_PER_EUR[fromCurrency];
  const toUnits = UNITS_PER_EUR[toCurrency];
  if (!fromUnits || !toUnits) return '1';

  return formatDefaultRate(toUnits / fromUnits);
}

const LANGUAGE_LOCALE = {
  en: 'en-GB',
  sr: 'sr-RS',
  hr: 'hr-HR',
  ro: 'ro-RO',
  hu: 'hu-HU',
  cs: 'cs-CZ',
  sk: 'sk-SK',
  uk: 'uk-UA',
};

export function formatLocalizedNumber(value, languageCode, decimals = 2) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = parseFloat(value);
  if (Number.isNaN(num)) {
    return '';
  }
  const locale = LANGUAGE_LOCALE[languageCode] || 'sr-RS';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - Currency code (e.g., 'EUR', 'USD')
 * @returns {string} - Currency symbol (e.g., '€', '$')
 */
export const getCurrencySymbol = (currencyCode = 'EUR') => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

/**
 * Format price with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @param {boolean} showSymbol - Whether to show currency symbol
 * @param {number} decimals - Number of decimal places (default 2, use 0 for whole numbers)
 * @returns {string} - Formatted price string
 */
export const formatPrice = (amount, currencyCode = 'EUR', showSymbol = true, decimals = 2) => {
  if (amount === null || amount === undefined || amount === '') return '';
  
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return '';

  const formattedNumber = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });

  if (!showSymbol) return formattedNumber;
  
  const symbol = getCurrencySymbol(currencyCode);
  
  // Position symbol based on currency
  // For most currencies, symbol goes after the number
  // For USD, GBP, EUR (in some regions), symbol goes before
  if (currencyCode === 'USD' || currencyCode === 'GBP') {
    return `${symbol}${formattedNumber}`;
  }
  
  // For EUR, RSD, HRK, HUF, CZK, RON - symbol after with space
  return `${formattedNumber} ${symbol}`;
};

/**
 * Get default currency from salon settings
 * @param {object} salonSettings - Salon settings object
 * @returns {string} - Currency code
 */
export const getSalonCurrency = (salonSettings) => {
  return salonSettings?.currency?.value || 'EUR';
};

